import React, {
  useState,
  forwardRef,
  useCallback,
  useRef,
  useEffect,
} from "react";
import styles from "./Search.module.scss";
import * as motion from "motion/react-m";
import { ReactSVG } from "react-svg";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { getRelativePathFromLink } from "../../../utils/url";
import { SearchResultItemType } from "@/components/SearchResult/useSearch";
import { useDebounce } from "@/hooks/useDebounce";
import {
  searchPagesForResults,
  searchRecipesForResults,
} from "@/utils/search/searchRecipesForResults";
import { ResultItem } from "@/components/SearchResult/ResultItem/ResultItem";
import { LoaderCircle } from "@/components/Global/LoaderCircle/LoaderCircle";
import { take } from "lodash";
import { Icon } from "@/components/Global/Icon/Icon";
import { useRouter } from "next/compat/router";
import useOnClickOutside from "@/hooks/useOnClickOutside";

interface SearchProps {
  searchPageSlug: string;
  isHero?: boolean;
  isFocussable?: boolean;
}

export const Search = forwardRef<HTMLDivElement, SearchProps>(
  ({ searchPageSlug, isHero = false, isFocussable = true }, ref) => {
    const [searchPhrase, setSearchPhrase] = useState("");
    const { getIcon, getLabel } = useGlobalContext();
    const innerRef = useRef<HTMLDivElement>(null);

    const router = useRouter();

    const [suggestionsShowing, setSuggestionsShowing] = useState(false);
    const [suggestions, setSuggestions] = useState<SearchResultItemType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    searchPageSlug = searchPageSlug
      ? getRelativePathFromLink(searchPageSlug).replace(/\//g, "")
      : "search";

    const searchHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        window.location.replace(`/${searchPageSlug}?keyword=` + searchPhrase);
      }
    };

    const searchPages = useCallback(
      async (value: string): Promise<void> => {
        const data = await searchPagesForResults(value, router?.locale);
        if (data && value === searchPhrase) {
          setSuggestions(take(data.suchergebnisse, 4));
        }
      },

      // eslint-disable-next-line react-hooks/exhaustive-deps
      [searchPhrase],
    );

    const fetchResults = useCallback(
      async (value: string): Promise<void> => {
        if (value.length < 4) {
          return;
        }
        setLoading(true);
        const data = await searchRecipesForResults(value, "", 1, 4);

        if (data && value === searchPhrase) {
          const allSearchResults =
            (data?.suchergebnisse as SearchResultItemType[]) || [];
          setSuggestions(
            take(
              allSearchResults.filter((x) => x.schwierigkeitsgrad !== 0),
              4,
            ),
          );
        } else if (value === searchPhrase) {
          await searchPages(value);
        }

        if (value === searchPhrase) {
          setLoading(false);
        }
      },

      [searchPhrase, searchPages],
    );

    const debouncedFetchResults = useDebounce(fetchResults, 500);

    const autoSuggest = (e: React.ChangeEvent<HTMLInputElement>): void => {
      const currentValue = e.target.value;
      setSearchPhrase(currentValue);
      if (currentValue.length >= 4) {
        setSuggestionsShowing(true);
        debouncedFetchResults(currentValue);
      } else {
        setSuggestionsShowing(false);
        setSuggestions([]);
      }
    };

    const Reset = () => {
      setSuggestionsShowing(false);
      setSuggestions([]);
      setSearchPhrase("");
      setLoading(false);
    };

    useEffect(() => {
      if (!ref) return;

      if (typeof ref === "function") {
        ref(innerRef.current);
      } else {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current =
          innerRef.current;
      }
    }, [ref]);

    useEffect(() => {
      if (!router) return;
      router.events.on("routeChangeStart", Reset);
      return () => {
        router.events.off("routeChangeStart", Reset);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useOnClickOutside(innerRef, Reset, "mousedown");

    return (
      // The forwarded ref from the parent component is attached here.
      <div
        data-suggestions={suggestionsShowing}
        ref={innerRef}
        className={`search-wrapper ${styles["search-wrapper"]} ${isHero ? styles["is-hero"] : ""}`}
      >
        <motion.form
          onSubmit={(e) => e.preventDefault()}
          className={`${styles["searchbar"]} `}
        >
          {isHero && (
            <ReactSVG
              width={32}
              height={32}
              src={getIcon("circleSearch")}
              wrapper={"svg"}
              aria-hidden
              beforeInjection={(svg) => {
                svg.setAttribute("alt", `Open Icon`);
                svg.setAttribute("aria-label", `Open Icon`);
              }}
            />
          )}

          <input
            className={`${styles["searchbar-input"]} input`}
            name="searchbar"
            placeholder={getLabel("search_placeholder")}
            aria-label={getLabel("search_input_aria_label")}
            autoComplete="off"
            onChange={autoSuggest}
            tabIndex={isFocussable ? 0 : -1}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                Reset();
              }
              searchHandler(e);
            }}
            // onBlur={() => Reset()}
          />
          {!isHero && (
            <ReactSVG
              src={getIcon("search")}
              width={24}
              height={24}
              wrapper={"svg"}
              className={`${styles["search-icon"]}`}
              aria-hidden
              beforeInjection={(svg) => {
                svg.setAttribute("alt", `Search Icon`);
                svg.setAttribute("aria-label", `Search Icon`);
              }}
            />
          )}

          <div className={styles["suggestions-wrapper"]}>
            {suggestions &&
              suggestions.map((suggestion, key) => {
                return (
                  <ResultItem
                    key={key}
                    result={suggestion}
                    cardType={
                      suggestion.produkttyp === "Tiptopf" ||
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (suggestion.produkttyp as any) === ""
                        ? "tip_topf"
                        : "kleine_tip_topf"
                    }
                  />
                );
              })}

            {loading && <LoaderCircle />}
            {!loading &&
              suggestions.length === 0 &&
              searchPhrase.length >= 4 && (
                <ResultItem
                  result={{
                    value_type: "no results",
                    value_titel: getLabel(
                      "search_no_results",
                      "Deine Suche ergab keine Treffer im kleinen Tiptopf. Entdecke dafür:",
                    ),
                    value_id: 0,
                    value_link: "",
                    value_slug: "",
                    value_category: [],
                    value_ernaehrungsform: [],
                    is_subpage: false,
                    produkttyp: "Der kleine Tiptopf",
                    schwierigkeitsgrad: 0,
                  }}
                />
              )}

            {!loading && (
              <button
                onClick={() => {
                  window.location.replace(
                    `/${searchPageSlug}?keyword=` + searchPhrase,
                  );
                }}
                className={styles["view-all-results"]}
              >
                <Icon src={getIcon("circleArrowRight")} ariaHidden />
                {getLabel("search_view_all_results", "Alle Ergebnisse für")}
              </button>
            )}
          </div>
        </motion.form>
      </div>
    );
  },
);

// Adding a displayName is a good practice for debugging with React DevTools.
Search.displayName = "Search";
